import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import buildRoute from "components/buildRoute";
import { trackEvent } from "../../../../configureMixpanel";
import PageHeader from "components/PageHeader";
import getCurrentUser from "hooks/getCurrentUser";
import FileSchemaEditor from "../components/FileSchemaEditor";
import ColumnCheckDrawer from "../components/ColumnCheckDrawer";
import * as api from "../../fileUploaderAdminApi";

const STORAGE_TYPES = ["gcs"];

// File extensions are constrained by the backend FileType model choices.
// Adding a new extension requires a backend code change and a new file reader.
const ACCEPTED_EXTENSIONS = [".csv", ".sav"];
const newFileTypeRow = () => ({ _rowId: `row-${Date.now()}-${Math.random()}`, extension: ".csv", encoding: "utf-8", skip_rows: 0, delimiter: "" });

const ENCODINGS = ["utf-8", "latin-16-le"];


const emptyForm = () => ({
  name: "",
  description: "",
  schema_name: "",
  schema_strict: false,
  file_type_rows: [],
  storage_location_uri: "",
  storage_location_type: "gcs",
  dataset_name: "pad_file_uploader",
  overwrite_existing: false,
});

// ── Schema preview accordion ─────────────────────────────────────────────────
function SchemaPreview({ columns, onEdit }) {
  const [expanded, setExpanded] = useState(false);
  if (!columns || columns.length === 0) return null;

  return (
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, mt: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 0.75,
          cursor: "pointer",
          backgroundColor: "grey.50",
          "&:hover": { backgroundColor: "secondary.light" },
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <Typography variant="body2" fontWeight={500}>
          Schema Preview — {columns.length} column{columns.length !== 1 ? "s" : ""}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
          <IconButton size="small" onClick={onEdit}>
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" tabIndex={-1} onClick={() => setExpanded((v) => !v)}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>
      <Collapse in={expanded}>
        <Divider />
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "secondary.light" }}>
              <TableCell>#</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Required</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {columns.map((col, idx) => (
              <TableRow key={col.id ?? col.name}>
                <TableCell sx={{ color: "text.secondary" }}>{idx + 1}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace">{col.name}</Typography>
                </TableCell>
                <TableCell><Chip label={col.data_type} size="small" /></TableCell>
                <TableCell>{col.is_required ? "✓" : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Collapse>
    </Box>
  );
}

// ── Inline Column Validation Rules ───────────────────────────────────────────
const RULE_TYPE_LABELS = { accepted_values: "Accepted Values", unique: "Unique" };

function ColumnValidationRules({ hasSchema, checks, onEditRule, onDeleteRule }) {
  if (!hasSchema) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
        Define the schema above to configure validation rules.
      </Typography>
    );
  }

  return (
    <Box>
      {checks.length > 0 ? (
        <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden", mb: 1.5 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "grey.50" }}>
              <TableCell>Column</TableCell>
              <TableCell>Rule Type</TableCell>
              <TableCell sx={{ maxWidth: 320 }}>Accepted Values</TableCell>
              <TableCell sx={{ minWidth: 140, whiteSpace: "nowrap" }}>Applies To</TableCell>
              <TableCell align="right" sx={{ minWidth: 72, whiteSpace: "nowrap", pr: 2 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {checks.map((cc) => (
              <TableRow key={cc._id}>
                <TableCell>
                  <Link
                    component="button"
                    variant="body2"
                    underline="hover"
                    color="primary"
                    fontFamily="monospace"
                    onClick={() => onEditRule(cc)}
                    sx={{ cursor: "pointer", background: "none", border: "none", p: 0 }}
                  >
                    {cc.column_name}
                  </Link>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {RULE_TYPE_LABELS[cc.check_type] ?? cc.check_type}
                  </Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 320 }}>
                  {cc.check_type === "accepted_values" && cc.values?.length > 0 ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {cc.values.map((v) => (
                        <Chip key={v} label={v} size="small" variant="outlined" />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">—</Typography>
                  )}
                </TableCell>
                <TableCell sx={{ minWidth: 140, whiteSpace: "nowrap" }}>
                  {cc.user_emails.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">All users</Typography>
                  ) : (
                    <Stack spacing={0.25}>
                      {cc.user_emails.map((email) => (
                        <Typography key={email} variant="body2" noWrap>{email}</Typography>
                      ))}
                    </Stack>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 72, whiteSpace: "nowrap" }}>
                  <IconButton size="small" onClick={() => onEditRule(cc)}>
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => onDeleteRule(cc._id)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          No rules configured.
        </Typography>
      )}
    </Box>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
const CreateEditTemplatePage = () => {
  const { projectId, templateId } = useParams();
  const navigate = useNavigate();
  const isEdit = !!templateId;

  const loggedInUser = getCurrentUser();
  const partner = loggedInUser?.partners?.find((p) => p.gcp_project_id === projectId);
  const partnerId = partner?.id;

  const [form, setForm] = useState(emptyForm());
  const [columns, setColumns] = useState([]);
  const [hasUploadedFiles, setHasUploadedFiles] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState("");

  // Schema editor drawer
  const [schemaEditorOpen, setSchemaEditorOpen] = useState(false);

  // Column check drawer
  const [checkDrawerOpen, setCheckDrawerOpen] = useState(false);
  const [editingCheck, setEditingCheck] = useState(null);
  const [lockedColumn, setLockedColumn] = useState(null);

  // ── Load initial data ──────────────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      try {
        if (isEdit) {
          const tmpl = await api.fetchTemplate(templateId);
          setForm({
            name: tmpl.name,
            description: tmpl.description ?? "",
            schema_name: tmpl.schema_name,
            schema_strict: tmpl.schema_strict,
            file_type_rows: tmpl.file_type_rows ?? [],
            storage_location_uri: tmpl.storage_location_uri,
            storage_location_type: tmpl.storage_location_type,
            dataset_name: tmpl.dataset_name,
            overwrite_existing: tmpl.overwrite_existing,
          });
          setColumns(tmpl.columns ?? []);
          setHasUploadedFiles(tmpl.has_uploaded_files ?? false);
        }
      } catch (err) {
        setSaveError(err.message || "Failed to load template.");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [isEdit, templateId]);

  // ── Form helpers ──────────────────────────────────────────────────────────
  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const addFileTypeRow = () =>
    setForm((f) => ({ ...f, file_type_rows: [...f.file_type_rows, newFileTypeRow()] }));

  const removeFileTypeRow = (rowId) =>
    setForm((f) => ({ ...f, file_type_rows: f.file_type_rows.filter((r) => r._rowId !== rowId) }));

  const updateFileTypeRow = (rowId, field, value) =>
    setForm((f) => ({
      ...f,
      file_type_rows: f.file_type_rows.map((r) =>
        r._rowId === rowId ? { ...r, [field]: value } : r
      ),
    }));

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Template name is required.";
    if (!form.schema_name.trim()) errs.schema_name = "Schema name is required.";
    if (form.file_type_rows.length === 0)
      errs.file_type_rows = "Add at least one accepted file type.";
    if (!form.storage_location_uri.trim())
      errs.storage_location_uri = "Storage location URI is required.";
    if (!partnerId) errs.partner = "Could not determine partner. Contact support.";
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    setSaveError("");
    try {
      await api.saveTemplate({ isEdit, templateId, form, columns, partnerId });
      trackEvent("file_template_saved", {
        mode: isEdit ? "edit" : "create",
        success: true,
        file_type_count: form.file_type_rows.length,
        column_check_count: allColumnChecks.length,
      });
      navigate(`/projects/${projectId}/file-uploader/admin`);
    } catch (err) {
      setSaveError(err.message || "Failed to save template.");
      trackEvent("file_template_saved", {
        mode: isEdit ? "edit" : "create",
        success: false,
      });
    } finally {
      setSaving(false);
    }
  };

  // ── Schema editor handlers ─────────────────────────────────────────────────
  const handleSchemaSave = useCallback((schemaData) => {
    setForm((f) => ({ ...f, schema_name: schemaData.name, schema_strict: schemaData.strict }));
    setColumns(schemaData.columns);
    setSchemaEditorOpen(false);
  }, []);

  // ── Column check handlers ─────────────────────────────────────────────────
  const openAddCheck = () => {
    setEditingCheck(null);
    setLockedColumn(null);
    setCheckDrawerOpen(true);
  };

  const openEditCheck = (cc) => {
    setEditingCheck(cc);
    setLockedColumn(null);
    setCheckDrawerOpen(true);
  };

  const handleCheckSave = useCallback(({ _editingId, column_name, ...rest }) => {
    const newCheck = { ...rest, _id: _editingId ?? `tmp-${Date.now()}` };
    setColumns((cols) =>
      cols.map((col) => {
        if (col.name !== column_name) return col;
        if (_editingId) {
          return {
            ...col,
            column_checks: (col.column_checks ?? []).map((cc) =>
              cc._id === _editingId ? newCheck : cc
            ),
          };
        }
        return { ...col, column_checks: [...(col.column_checks ?? []), newCheck] };
      })
    );
    setCheckDrawerOpen(false);
    setEditingCheck(null);
    setLockedColumn(null);
  }, []);

  const handleDeleteCheck = useCallback((_id) => {
    setColumns((cols) =>
      cols.map((col) => ({
        ...col,
        column_checks: (col.column_checks ?? []).filter((cc) => cc._id !== _id),
      }))
    );
  }, []);

  // ── Derived state ─────────────────────────────────────────────────────────
  const allColumnChecks = columns.flatMap((col) =>
    (col.column_checks ?? []).map((cc) => ({ ...cc, column_name: col.name }))
  );

  const hasSchema = form.schema_name.trim().length > 0 || columns.length > 0;

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const pageCrumb = isEdit ? (form.name || "Edit Template") : "New Template";

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link
          component={RouterLink}
          to={`/projects/${projectId}/file-uploader/admin`}
          underline="hover"
          color="inherit"
          variant="body2"
        >
          File Templates
        </Link>
        <Typography variant="body2" color="text.primary">{pageCrumb}</Typography>
      </Breadcrumbs>

      <PageHeader
        title={isEdit ? "Edit Template" : "New File Template"}
        subtitle="Configure the schema, accepted file types, and storage destination for this template."
      />

      {saveError && <Alert severity="error" sx={{ mb: 3 }}>{saveError}</Alert>}
      {errors.partner && <Alert severity="error" sx={{ mb: 3 }}>{errors.partner}</Alert>}

      <Stack spacing={3} sx={{ maxWidth: 860 }}>

        {/* ── Template Details ─────────────────────────────────────── */}
        <Card variant="outlined">
          <CardHeader title="Template Details" titleTypographyProps={{ variant: "subtitle1" }} />
          <CardContent>
            <Stack spacing={2.5}>
              <TextField
                label="Template Name"
                value={form.name}
                onChange={set("name")}
                required
                fullWidth
                error={!!errors.name}
                helperText={errors.name}
                placeholder="e.g. Paid Media Campaign Metrics"
              />
              <TextField
                label="Description"
                value={form.description}
                onChange={set("description")}
                fullWidth
                multiline
                minRows={2}
                placeholder="Optional description for this template"
              />

              {/* ── Accepted File Types ───────────────────────────── */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                  Accepted File Types
                </Typography>
                {errors.file_type_rows && (
                  <Alert severity="error" sx={{ mb: 1.5 }}>{errors.file_type_rows}</Alert>
                )}
                {form.file_type_rows.length === 0 && (
                  <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ mb: 1.5 }}>
                    No file types added. Click the button below to add an accepted file type.
                  </Typography>
                )}
                {form.file_type_rows.length > 0 && (
                  <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden", mb: 1.5 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "grey.50" }}>
                          <TableCell sx={{ width: 120 }}>Extension</TableCell>
                          <TableCell sx={{ width: 180 }}>Encoding</TableCell>
                          <TableCell sx={{ width: 110 }}>Skip Rows</TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              Delimiter
                              <Tooltip title="Override the column delimiter. Leave blank to use the default for this file type (e.g. comma for CSV, tab for TSV).">
                                <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.secondary", cursor: "help" }} />
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: "nowrap", pr: 2 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {form.file_type_rows.map((row) => (
                          <TableRow key={row._rowId} sx={{ "&:last-child td": { border: 0 } }}>
                            <TableCell sx={{ py: 1.5 }}>
                              <FormControl size="small" fullWidth>
                                <Select
                                  value={row.extension}
                                  onChange={(e) => updateFileTypeRow(row._rowId, "extension", e.target.value)}
                                >
                                  {ACCEPTED_EXTENSIONS.map((ext) => (
                                    <MenuItem key={ext} value={ext}>{ext.replace(".", "").toUpperCase()}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <FormControl size="small" fullWidth>
                                <Select
                                  value={row.encoding || "utf-8"}
                                  onChange={(e) => updateFileTypeRow(row._rowId, "encoding", e.target.value)}
                                >
                                  {ENCODINGS.map((enc) => (
                                    <MenuItem key={enc} value={enc}>{enc}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <TextField
                                size="small"
                                type="number"
                                value={row.skip_rows ?? 0}
                                onChange={(e) =>
                                  updateFileTypeRow(row._rowId, "skip_rows", Math.max(0, parseInt(e.target.value, 10) || 0))
                                }
                                inputProps={{ min: 0, style: { textAlign: "center" } }}
                                helperText="Inclusive of column header. If your file has a header row, use 0."
                                sx={{ width: 80 }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <TextField
                                size="small"
                                value={row.delimiter ?? ""}
                                onChange={(e) => updateFileTypeRow(row._rowId, "delimiter", e.target.value)}
                                placeholder="auto"
                                inputProps={{ style: { fontFamily: "monospace" } }}
                                sx={{ width: 90 }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ pr: 2 }}>
                              <IconButton size="small" color="error" onClick={() => removeFileTypeRow(row._rowId)}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addFileTypeRow}>
                    Add File Type
                  </Button>
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* ── File Schema ──────────────────────────────────────────── */}
        <Card variant="outlined">
          <CardHeader
            title="File Schema"
            titleTypographyProps={{ variant: "subtitle1" }}
            subheader="Defines the expected column structure for files uploaded with this template."
            subheaderTypographyProps={{ variant: "body2" }}
          />
          <CardContent>
            <Stack spacing={1.5}>
              {errors.schema_name && (
                <Alert severity="error">{errors.schema_name}</Alert>
              )}

              {!form.overwrite_existing && hasUploadedFiles && (
                <Alert severity="info" icon={false}>
                  Files have been uploaded to this template and overwrite is disabled. Column
                  names and types are locked — you can add new columns freely, or enable{" "}
                  <strong>Overwrite existing files</strong> to unlock all edits.
                </Alert>
              )}

              {hasSchema ? (
                <>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" fontWeight={500}>{form.schema_name}</Typography>
                    {form.schema_strict && (
                      <Chip label="Strict" size="small" color="warning" variant="outlined" />
                    )}
                  </Box>
                  <SchemaPreview
                    columns={columns}
                    onEdit={() => setSchemaEditorOpen(true)}
                  />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  No schema configured. Click the button below to define the schema for this template.
                </Typography>
              )}

              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setSchemaEditorOpen(true)}
                >
                  Create New Schema
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* ── Column Validation Rules ──────────────────────────────── */}
        <Card variant="outlined">
          <CardHeader
            title="Column Validation Rules"
            titleTypographyProps={{ variant: "subtitle1" }}
            subheader="Optional: restrict which values specific users may upload per column."
            subheaderTypographyProps={{ variant: "body2" }}
          />
          <CardContent>
            <Stack spacing={1.5}>
              <ColumnValidationRules
                hasSchema={hasSchema}
                checks={allColumnChecks}
                onEditRule={openEditCheck}
                onDeleteRule={handleDeleteCheck}
              />
              {hasSchema && (
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={openAddCheck}
                  >
                    Add Validation Rule
                  </Button>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* ── Storage Configuration ────────────────────────────────── */}
        <Card variant="outlined">
          <CardHeader
            title="Storage Configuration"
            titleTypographyProps={{ variant: "subtitle1" }}
          />
          <CardContent>
            <Stack spacing={2.5}>
              <TextField
                label="GCS Bucket Name"
                value={form.storage_location_uri}
                onChange={set("storage_location_uri")}
                required
                fullWidth
                error={!!errors.storage_location_uri}
                helperText={errors.storage_location_uri || "The GCS bucket name where uploaded files will be stored. Default CTA-managed bucket: pad-ui-file-uploader"}
                placeholder="pad-ui-file-uploader"
              />
              <Stack direction="row" spacing={2}>
                <FormControl sx={{ minWidth: 160 }}>
                  <InputLabel>Storage Type</InputLabel>
                  <Select
                    value={form.storage_location_type}
                    onChange={set("storage_location_type")}
                    label="Storage Type"
                  >
                    {STORAGE_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>{t.toUpperCase()}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Dataset Name"
                  value={form.dataset_name}
                  onChange={set("dataset_name")}
                  fullWidth
                  helperText="BigQuery dataset name for downstream ingestion"
                />
              </Stack>
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.overwrite_existing}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, overwrite_existing: e.target.checked }))
                      }
                    />
                  }
                  label="Overwrite existing files"
                />
                {!form.overwrite_existing && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    With overwrite disabled, column deletions and type changes are blocked once
                    files have been uploaded to this template.
                  </Alert>
                )}
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* ── Actions ──────────────────────────────────────────────── */}
        <Box sx={{ display: "flex", gap: 2, pb: 6 }}>
          <Button
            onClick={() => navigate(`/projects/${projectId}/file-uploader/admin`)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={22} /> : isEdit ? "Save Changes" : "Save Template"}
          </Button>
        </Box>
      </Stack>

      {/* Schema editor drawer */}
      <FileSchemaEditor
        open={schemaEditorOpen}
        onClose={() => setSchemaEditorOpen(false)}
        onSave={handleSchemaSave}
        initialSchema={
          hasSchema
            ? { name: form.schema_name, strict: form.schema_strict, columns }
            : null
        }
        overwriteEnabled={form.overwrite_existing}
        hasUploadedFiles={hasUploadedFiles}
      />

      {/* Column check drawer */}
      <ColumnCheckDrawer
        open={checkDrawerOpen}
        onClose={() => {
          setCheckDrawerOpen(false);
          setEditingCheck(null);
          setLockedColumn(null);
        }}
        onSave={handleCheckSave}
        columns={columns}
        initialData={editingCheck}
        lockedColumnName={lockedColumn}
      />
    </Box>
  );
};

export default buildRoute({ element: <CreateEditTemplatePage /> });
